import { type FunctionComponent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { MEDIUM_MAX_PX, SMALL_MAX_PX } from '../../constants/breakpoints';
import { MediaGalleryContextProvider } from '../../contexts/pdp/media-gallery.context';
import { type CloudinaryOptions, generateCloudinaryUrl } from '../../helpers/cloudinary-helper/cloudinary-helper';
import { type Media, isImage, isThreeSixtyImage, isVideo, type Variation } from '../../helpers/images/images.helper';
import { ImageGalleryCarousel } from '../carousel-components/image-gallery-carousel/image-gallery-carousel.component';
import { CloudinaryImage } from '../images/cloudinary-image/cloudinary-image.component';
import { MediaGalleryModal } from './media-gallery-modal/media-gallery-modal.component';
import { imageContainer, imageGalleryWidth, videoCardMaxHeight } from './media-gallery.css';

export type MediaGalleryProps = {
	items: Media[];
	variantImageId?: string;
	title?: string;
	currentVariantImage?: Media;
};

export const MediaGallery: FunctionComponent<MediaGalleryProps> = ({ items, variantImageId, title = '', currentVariantImage }) => {
	const [showModal, setShowModal] = useState(false);
	const initItem = items.length > 0 ? items[0] : null;
	const [currentMedia, setCurrentMedia] = useState(initItem);

	const defaultOptions: CloudinaryOptions = { width: 450, height: 450 };

	const variations: Variation[] = [
		{ mediaCondition: { maxWidth: SMALL_MAX_PX }, options: { width: 330, height: 330 } },
		{ mediaCondition: { maxWidth: MEDIUM_MAX_PX }, options: { width: 450, height: 450 } }
	];

	const selectedVariantImageUrl = generateCloudinaryUrl(currentVariantImage?.id || '', {
		...defaultOptions,
		...variations,
		crop: 'pad',
		dpr: 2
	});

	useEffect(() => {
		if (currentVariantImage) {
			// will overwrite main image if a variant is clicked on
			setCurrentMedia(currentVariantImage);
		}
	}, [variantImageId]);

	return (
		<>
			<Helmet>
				<link rel="preload" as="image" href={selectedVariantImageUrl} />
			</Helmet>
			<MediaGalleryContextProvider>
				<div className={`chromatic-ignore bg-theme-white relative mt2-l`}>
					{currentVariantImage?.id !== variantImageId ? (
						<div
							className="absolute z-5 bg-theme-white left-0 right-0 top-0 bottom-0 flex items-center justify-center"
							data-testid="current-variant-image"
							style={{ maxHeight: defaultOptions.height }}>
							<CloudinaryImage
								description={currentVariantImage && isImage(currentVariantImage) ? currentVariantImage?.description : ''}
								publicID={currentVariantImage?.id || ''}
								options={defaultOptions}
								variations={variations}
								className={'bg-theme-white'}
							/>
						</div>
					) : null}
					<div className={`${imageGalleryWidth} ${imageContainer}  h-auto-p`} role="region" aria-label="main image">
						{currentMedia && (
							<ImageGalleryCarousel
								items={items}
								thumbnailMode="show"
								selectedItemId={currentMedia.id}
								onSelectedImageChange={setCurrentMedia}
								onClick={() => setShowModal(!isVideo(currentMedia) && !isThreeSixtyImage(currentMedia))}
								galleryImageOptions={{ options: defaultOptions, variations, bypassLazyLoad: true }}
								shouldOpenVideoInModal={true}
								videoStyling={videoCardMaxHeight}
							/>
						)}
					</div>
					{showModal && currentMedia && (
						<MediaGalleryModal
							items={items}
							onClose={() => setShowModal(false)}
							title={title}
							currentMediaImage={currentMedia}
							onSelectedMediaChange={setCurrentMedia}
							shouldOpenVideoInModal={false}
						/>
					)}
				</div>
			</MediaGalleryContextProvider>
		</>
	);
};
// min height for small is 452 and large is 572
